import React, { useEffect, useRef, useState } from "react";
import { Timer, Trophy, MessageCircle } from "lucide-react";
import { shareLuckyResult } from "./LuckyHotNumbers";
import api from "../lib/api";
import { Reels } from "./GameFX";
import { celebrateWin } from "../lib/celebrate";
import { haptic } from "../lib/haptics";
import { sfx } from "../lib/sound";
import { fmt } from "../lib/format";
import { StoryButton } from "./StoryButton";
import { useMT } from "../lib/miniI18n";
import { RoyalStage } from "./RoyalStage";
import { slotLabel } from "../lib/slotVibe";
import { useI18n } from "../i18n";

const hm = (s) => s.split(":").map(Number);
const pad = (n) => String(n).padStart(2, "0");
export const fmtLeft = (sec) => `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;

export function slotsOf(game) {
  if (game.mode !== "interval") return [game.draw_time || "20:00"];
  const [oh, om] = hm(game.open_time || "09:00"), [lh, lm] = hm(game.last_draw_time || game.draw_time || "22:00");
  const step = game.interval_minutes || 30, out = [];
  for (let t = oh * 60 + om + step; t <= lh * 60 + lm; t += step) out.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`);
  const off = new Set(game.disabled_slots || []);
  return out.filter((t) => !off.has(t));
}

/* Live round panel for interval games: "Result in mm:ss" → spinning reels → number reveal (+ win celebration). */
export default function LuckyLive({ game, onSettled, compact = false }) {
  const rt = game.runtime || {};
  const mt = useMT();
  const { lang } = useI18n();
  const offset = useRef(0);
  const [now, setNow] = useState(() => Date.now());
  const [live, setLive] = useState(null); // { slot, phase: waiting|drawing|reveal, number, won }
  const polled = useRef(0);
  const mountedAt = useRef(Date.now());

  useEffect(() => { if (rt.server_time) offset.current = new Date(String(rt.server_time).slice(0, 23)).getTime() - Date.now(); }, [rt.server_time]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const ist = new Date(now + offset.current);
  const dayStart = new Date(ist.getFullYear(), ist.getMonth(), ist.getDate()).getTime();
  const fixed = game.mode !== "interval";
  const closeBefore = fixed ? Math.max(60000, ((hm(game.draw_time || "20:00")[0] * 60 + hm(game.draw_time || "20:00")[1]) - (hm(game.close_time || "19:55")[0] * 60 + hm(game.close_time || "19:55")[1])) * 60000) : (game.close_before_minutes || 5) * 60000;
  const nextSlot = fixed ? game.draw_time : rt.draw_slot;
  const today = `${ist.getFullYear()}-${pad(ist.getMonth() + 1)}-${pad(ist.getDate())}`;
  let cur = null;
  for (const s of slotsOf(game)) {
    const [h, m] = hm(s); const drawMs = dayStart + (h * 60 + m) * 60000;
    if (ist.getTime() >= drawMs - closeBefore && ist.getTime() < drawMs + 120000) { cur = { slot: s, drawMs }; break; }
  }

  useEffect(() => {
    if (live?.phase === "reveal" && now - live.at > 90000) { setLive(null); return; }
    if (!cur) { if (live && live.phase !== "reveal") setLive(null); return; }
    if (!live || live.slot !== cur.slot) {
      // arrived after the draw already happened → fetch quietly and show the number static (no re-spin)
      const late = mountedAt.current + offset.current >= cur.drawMs;
      setLive({ slot: cur.slot, phase: late ? "drawing" : "waiting", late }); return;
    }
    if (live.phase === "waiting" && ist.getTime() >= cur.drawMs) { setLive({ ...live, phase: "drawing" }); sfx.rattle(2); }
    if (live.phase === "drawing" && !live.late && ist.getTime() >= cur.drawMs + 75000) { setLive({ ...live, late: true }); } // reels spin only in the first 75 s after the draw
    if (live.phase === "drawing" && now - polled.current > 4000) {
      polled.current = now;
      api.get("/results", { params: { game_id: game.id, date: today, limit: 10 } }).then(async ({ data }) => {
        const r = (data || []).find((x) => x.draw_time === cur.slot);
        if (!r) return;
        let won = 0;
        try { const { data: bets } = await api.get("/bets/my", { params: { tab: "won" } }); won = (bets || []).filter((b) => b.game_id === game.id && b.draw_date === today && (fixed || b.draw_time === cur.slot) && b.status === "won").reduce((a, b) => a + (b.win_amount || 0), 0); } catch {}
        if (live.late || Date.now() - mountedAt.current < 1500) { setLive({ slot: cur.slot, phase: "reveal", number: r.winning_number, won, at: Date.now(), resultId: r.id, late: true }); onSettled && onSettled(); return; }
        setLive({ slot: cur.slot, phase: "spin", number: r.winning_number, won, resultId: r.id });
        setTimeout(() => {
          setLive({ slot: cur.slot, phase: "reveal", number: r.winning_number, won, at: Date.now(), resultId: r.id });
          haptic.land(); if (won > 0) celebrateWin(won, 0); else sfx.tap();
          onSettled && onSettled();
        }, 2600);
      }).catch(() => {});
    }
  }, [now]); // eslint-disable-line

  if (!live && !rt.is_open) return null;
  if (!live && compact) return null;
  if (!live) {
    const [nh, nm] = hm(nextSlot || "00:00");
    const nextSecs = Math.max(0, Math.round((dayStart + (nh * 60 + nm) * 60000 - ist.getTime()) / 1000));
    return (
      <div className="rl-card px-4 py-2.5 flex items-center justify-between" data-testid="lucky-next-result">
        <div className="flex items-center gap-2 text-xs text-slate-600"><Timer className="w-4 h-4 text-blue-600" />{mt(fixed ? "Result at" : "Next result")} <b className="text-slate-900">{nextSlot}</b></div>
        <span className="font-mono font-black text-blue-600" data-testid="lucky-result-countdown">{fmtLeft(nextSecs)}</span>
      </div>
    );
  }
  const secs = Math.max(0, Math.round(((cur?.drawMs || 0) - ist.getTime()) / 1000));
  const digits = Math.max(4, String(live.number || game.last_result?.winning_number || "").length);
  return (
    <RoyalStage drawTime={live.slot} dense className={`rounded-2xl ${compact ? "p-3" : "p-4"} shadow-lg text-center`} testId={compact ? "home-lucky-live" : "lucky-live-panel"}>
      <div className="text-[10px] uppercase tracking-widest text-amber-200 flex items-center justify-center gap-1"><Timer className="w-3 h-3" />{game.name} · {mt(fixed ? "SHOW" : "ROUND")} {live.slot} · LIVE</div>
      <div className="text-[9px] tracking-widest text-white/60 mt-0.5" data-testid="lucky-live-vibe">✦ {slotLabel(live.slot, lang)} ✦</div>
      {live.phase === "waiting" && (
        <>
          <div className="text-xs text-amber-100 mt-1">{mt("Booking closed · result in")}</div>
          <div className={`font-mono font-black ${compact ? "text-3xl" : "text-5xl"} text-amber-300 my-1 rl-pop`} data-testid="lucky-live-countdown">{fmtLeft(secs)}</div>
        </>
      )}
      {(live.phase === "drawing" || live.phase === "spin") && (
        <div className={`flex flex-col items-center gap-2 mt-2 ${compact ? "scale-[.8] -my-2" : ""}`}>
          {live.late ? <div className="text-xs text-amber-200" data-testid="lucky-live-awaited">{mt("Result awaited")}</div> : <><Reels value={live.number || 0} spinning digits={digits} title={`${game.name} · ${live.slot}`} /><div className="text-xs text-amber-200 animate-pulse">{mt("Drawing the winning number…")}</div></>}
        </div>
      )}
      {live.phase === "reveal" && (
        <div className={`flex flex-col items-center gap-2 mt-2 ${live.late ? "" : "rl-pop"} ${compact ? "scale-[.8] -my-2" : ""}`} data-testid="lucky-live-reveal" data-late={live.late ? "1" : "0"}>
          <Reels value={live.number} spinning={false} digits={digits} title={`${game.name} · ${live.slot}`} />
          <div className="text-xs text-amber-100">{mt("Result")} <b className="text-white text-base">{live.number}</b></div>
          {live.won > 0 ? <div className="flex items-center gap-1.5 bg-emerald-500 text-white font-display font-black px-4 py-1.5 rounded-full text-sm shadow" data-testid="lucky-live-won"><Trophy className="w-4 h-4" />{mt("YOU WON")} {fmt(live.won)}</div> : <div className="text-[11px] text-white/70">{mt(fixed ? "Better luck in the next show!" : "Next round opens now — good luck!")}</div>}
          <div className="mt-1 flex items-center gap-1.5">
            <button onClick={() => shareLuckyResult(game, live.slot, live.number, fixed ? null : rt.draw_slot)} className="bg-[#25D366] text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1" data-testid="lucky-live-share"><MessageCircle className="w-3.5 h-3.5" />{mt("Share result")}</button>
            {fixed && <StoryButton resultId={live.resultId} />}
          </div>
        </div>
      )}
    </RoyalStage>
  );
}
